// M1 throwaway: case template catalog formatter for the Case Analytics chat
// system prompt. Formats the workspace templates list (from React Query —
// same fetch as useCaseAnalyticsLabels) into a markdown block listing
// case_properties, actions, tasks, and blocks per template version.

import type {
  CaseBlockConfigOutput,
  CasePropertiesValue,
  CaseTemplateActionOutput,
  CaseTemplatesResponse,
  CaseTemplateTaskOutput,
  CaseTemplateVersionAttributes,
  EnumPropertyOutput,
} from "src/clients/case-management";

const MAX_TEMPLATE_VERSIONS = 30;
// agent-management rejects system prompts over 100k chars. The static case-analytics
// prompt template is ~30k chars, so budget the catalog block well below the
// remaining ~70k.
const MAX_SCHEMA_BLOCK_CHARS = 60_000;

type TemplateVersionEntry = {
  templateId: string;
  templateName: string;
  versionId: string;
  versionName: string;
  archived: boolean;
  attributes: CaseTemplateVersionAttributes;
};

const escapeCell = (value: string): string => value.replace(/\|/g, "\\|");

const isEnumProperty = (
  prop: CasePropertiesValue,
): prop is EnumPropertyOutput => prop.type === "enum";

const formatEnumValues = (prop: EnumPropertyOutput): string =>
  prop.values
    .map((v) =>
      v.display_name && v.display_name !== v.value
        ? `\`${v.value}\` (${v.display_name})`
        : `\`${v.value}\``,
    )
    .join(", ");

const formatProperty = (key: string, prop: CasePropertiesValue): string => {
  const label = escapeCell(prop.display_name || key);
  if (isEnumProperty(prop)) {
    const values = formatEnumValues(prop);
    return values.length > 0
      ? `\`${key}\` — enum "${label}": ${values}`
      : `\`${key}\` — enum "${label}"`;
  }
  return `\`${key}\` — ${prop.type} "${label}"`;
};

const formatKeyDisplayMap = (
  label: string,
  entries: Array<{ key: string; displayName: string }>,
): string | null => {
  if (entries.length === 0) return null;
  return `${label}: ${entries.map((e) => `\`${e.key}\` → ${escapeCell(e.displayName)}`).join(", ")}`;
};

const formatActions = (
  actions: Record<string, CaseTemplateActionOutput>,
): string | null =>
  formatKeyDisplayMap(
    "Actions",
    Object.values(actions).map((a) => ({
      key: a.key,
      displayName: a.display_name,
    })),
  );

const formatTasks = (
  tasks: Record<string, CaseTemplateTaskOutput>,
): string | null =>
  formatKeyDisplayMap(
    "Tasks",
    Object.values(tasks).map((t) => ({
      key: t.key,
      displayName: t.display_name,
    })),
  );

const formatBlocks = (
  blocks: Record<string, CaseBlockConfigOutput>,
): string | null => {
  const entries = Object.entries(blocks).map(([key, block]) => ({
    key,
    displayName: block.title || key,
  }));
  return formatKeyDisplayMap("Blocks", entries);
};

const collectTemplateVersions = (
  response: CaseTemplatesResponse,
): TemplateVersionEntry[] => {
  const { data, included } = response;
  if (!data?.length || !included?.length) return [];

  return data.flatMap((template) => {
    const versionRefs = template.relationships?.versions?.data;
    if (!Array.isArray(versionRefs)) return [];

    return versionRefs.flatMap((versionRef): TemplateVersionEntry[] => {
      const version = included.find((item) => item.id === versionRef.id);
      if (!version?.attributes) return [];

      return [
        {
          templateId: template.id,
          templateName: template.attributes.template_name,
          versionId: version.id,
          versionName: version.attributes.version_name,
          archived: version.attributes.archived ?? false,
          attributes: version.attributes,
        },
      ];
    });
  });
};

// Archived versions sort last.
const sortTemplateVersions = (
  entries: TemplateVersionEntry[],
): TemplateVersionEntry[] =>
  entries.toSorted((a, b) => {
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    const byTemplate = a.templateName.localeCompare(b.templateName);
    if (byTemplate !== 0) return byTemplate;
    return a.versionName.localeCompare(b.versionName);
  });

const formatVersionSection = (entry: TemplateVersionEntry): string => {
  const { attributes } = entry;
  const properties = Object.entries(attributes.case_properties ?? {}).map(
    ([key, prop]) => formatProperty(key, prop),
  );
  const actions = formatActions(attributes.case_actions ?? {});
  const tasks = formatTasks(attributes.case_tasks ?? {});
  const blocks = formatBlocks(attributes.case_blocks_configs ?? {});

  const archivedNote = entry.archived ? " _(archived)_" : "";
  const header = `### ${escapeCell(entry.templateName)} — ${escapeCell(entry.versionName)}${archivedNote}`;
  const ids = `- \`template_id\`: \`${entry.templateId}\` · \`template_version_id\`: \`${entry.versionId}\``;
  const propertyLines =
    properties.length > 0
      ? [
          "- **case_properties** (parse from event JSON: `(case_properties::JSON)->>'key'`):",
          ...properties.map((line) => `  - ${line}`),
        ]
      : ["- **case_properties**: _(none defined)_"];

  const extras = [actions, tasks, blocks].filter(
    (line): line is string => line !== null,
  );

  return [
    header,
    ids,
    ...propertyLines,
    ...extras.map((line) => `- ${line}`),
  ].join("\n");
};

export const formatCaseTemplateSchemaBlock = (
  response: CaseTemplatesResponse,
): string => {
  const allVersions = sortTemplateVersions(collectTemplateVersions(response));

  if (allVersions.length === 0) {
    return "_(no case templates in this workspace — discover `case_properties` keys from `case_history_events` on demand)_";
  }

  const allSections = allVersions
    .slice(0, MAX_TEMPLATE_VERSIONS)
    .map(formatVersionSection);
  const sections = allSections.reduce<string[]>((kept, section) => {
    const keptChars = kept.reduce((sum, s) => sum + s.length + 1, 0);
    if (kept.length === 0 && section.length > MAX_SCHEMA_BLOCK_CHARS) {
      return [
        `${section.slice(0, MAX_SCHEMA_BLOCK_CHARS)}\n- _(section truncated — discover remaining keys from events on demand)_`,
      ];
    }
    if (kept.length > 0 && keptChars + section.length > MAX_SCHEMA_BLOCK_CHARS)
      return kept;
    return [...kept, section];
  }, []);

  const truncated = sections.length < allVersions.length;
  const truncationNote = truncated
    ? `\n_(Showing ${sections.length} of ${allVersions.length} template versions — filter by \`template_id\` when scoping; discover additional keys from events if needed.)_`
    : "";

  return [
    "**Case template catalog (loaded at session start).** Property keys on events live in the `case_properties` JSON snapshot on each row — use the keys below when parsing (`(case_properties::JSON)->>'status'`, etc.). Enum values are the **stored** values; charts may also show legacy values from older template versions. Action / task / block keys map to flat event columns (`action_key`, `task_key`, `block_key`) or to template config. **Filter by `template_id` (and usually `template_version_id`) when the user names a template** — property definitions differ per version.",
    "",
    ...sections,
    truncationNote,
  ]
    .filter((line) => line !== "")
    .join("\n");
};

export const caseTemplateCatalogUnavailableBlock = (reason: string): string =>
  `_(case template catalog unavailable — ${reason}; discover \`case_properties\` keys from events on demand)_`;
